import { createBrowserRouter, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { Loading, RouteErrorBoundary } from '@/components/common'
import { PublicRoute } from '@/components/auth'
import {
  PageLoader,
  ProtectedRoute,
  EncryptedRoute,
  AppLayout,
} from '@/components/routing'

// Lazy load pages for code splitting
const Landing = lazy(() => import('@/pages/Landing/Landing'))
const Login = lazy(() => import('@/pages/Auth/Login'))
const Register = lazy(() => import('@/pages/Auth/Register'))
const WaitingForConsultant = lazy(() => import('@/pages/Auth/WaitingForConsultant'))
const Unlock = lazy(() => import('@/pages/Auth/Unlock'))
const Dashboard = lazy(() => import('@/pages/Dashboard/Dashboard'))
const OrderList = lazy(() => import('@/pages/Orders/OrderList'))
const OrderDetail = lazy(() => import('@/pages/Orders/OrderDetail'))
const AppointmentList = lazy(() => import('@/pages/Appointments/AppointmentList'))
const AppointmentDetail = lazy(() => import('@/pages/Appointments/AppointmentDetail'))
const DocumentList = lazy(() => import('@/pages/Documents/DocumentList'))
const DocumentDetail = lazy(() => import('@/pages/Documents/DocumentDetail'))
const OfferList = lazy(() => import('@/pages/Offers/OfferList'))
const OfferDetail = lazy(() => import('@/pages/Offers/OfferDetail'))
const RecommendationList = lazy(() => import('@/pages/Recommendations/RecommendationList'))
const Profile = lazy(() => import('@/pages/Profile/Profile'))

// Router configuration
export const router = createBrowserRouter([
  // Public landing page - accessible at root
  {
    path: '/',
    element: (
      <PublicRoute>
        <Suspense fallback={<PageLoader />}>
          <Landing />
        </Suspense>
      </PublicRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Public routes - no authentication required
  {
    path: '/login',
    element: (
      <PublicRoute>
        <Suspense fallback={<PageLoader />}>
          <Login />
        </Suspense>
      </PublicRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/register',
    element: (
      <PublicRoute>
        <Suspense fallback={<PageLoader />}>
          <Register />
        </Suspense>
      </PublicRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Protected but not encrypted routes - requires auth, not encryption unlock
  {
    path: '/waiting',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <WaitingForConsultant />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/unlock',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <Unlock />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Encrypted routes with app layout - requires auth AND encryption unlocked
  {
    path: '/dashboard',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
    ],
  },
  {
    path: '/orders',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <OrderList />,
      },
      {
        path: ':id',
        element: <OrderDetail />,
      },
    ],
  },
  {
    path: '/appointments',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <AppointmentList />,
      },
      {
        path: ':id',
        element: <AppointmentDetail />,
      },
    ],
  },
  {
    path: '/offers',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <OfferList />,
      },
      {
        path: ':id',
        element: <OfferDetail />,
      },
    ],
  },
  {
    path: '/recommendations',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <RecommendationList />,
      },
    ],
  },
  {
    path: '/documents',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <DocumentList />,
      },
      {
        path: ':id',
        element: <DocumentDetail />,
      },
    ],
  },
  {
    path: '/profile',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Profile />,
      },
      {
        path: 'preferences',
        element: <Navigate to="/profile" replace />,
      },
    ],
  },

  // OAuth callback route
  {
    path: '/oauth/callback',
    element: (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loading size="lg" text="Completing sign in..." />
      </div>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Catch-all - show error boundary for unknown routes
  {
    path: '*',
    element: <RouteErrorBoundary />,
  },
])

export default router
